import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  api, withQuery, money, quantity, date, PAYMENT_LABEL, STATUS_LABEL, STATUS_CLASS,
} from '../api.js';
import { usePaymentMethods } from '../hooks.js';
import { Pager } from './Products.jsx';

/**
 * Връщане на стоки — частично, не цял документ.
 *
 * За разлика от „Сторно" (целият документ се огледалва), тук операторът
 * връща само конкретен ред или част от количеството му, без да пипа
 * останалото по продажбата/доставката. Всеки връщан ред пази към кой
 * оригинален ред сочи (`returnOfItemId`) — оттам знае системата колко
 * вече е върнато и какъв УНП да носи Сторно-ФБ, когато фискалното
 * устройство се включи (Приложение № 29, т. 10).
 *
 * Затова връщането на продажба винаги е към САМО един оригинален
 * документ: редовете, добавени за връщане, се групират по документ и се
 * пращат като отделни записи, ако идват от повече от една продажба.
 */
export default function Returns() {
  const navigate = useNavigate();
  const { methods } = usePaymentMethods();

  const [tab, setTab] = useState('sale'); // 'sale' | 'purchase'
  // В адреса (?old=…), не в локално състояние — иначе "Назад" след
  // отваряне на старо връщане се връщаше на празната форма, не на
  // списъка, от който е тръгнал операторът.
  const [searchParams] = useSearchParams();
  const oldView = searchParams.get('old'); // null | 'sale' | 'purchase'
  const [partner, setPartner] = useState(null);
  const [partnerQuery, setPartnerQuery] = useState('');
  const [partnerHits, setPartnerHits] = useState([]);
  const [paidFilter, setPaidFilter] = useState('all');
  const [productQuery, setProductQuery] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cart, setCart] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('bank');
  const [issueInvoice, setIssueInvoice] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [balance, setBalance] = useState(null);
  const partnerInput = useRef(null);
  /*
   * Прозорецът с документа за връщане — като в старата програма: „Върни"
   * на ред го отваря, в него се вижда какво се връща, оттам се
   * записва. Иначе редът тихо слизаше в списък в дъното на страницата и
   * изглеждаше, че нищо не става.
   *   'doc'    — документът за връщане
   *   'reason' — причината за сторно бона
   *   'saved'  — записано; напомняне за сторно бона на ръка
   */
  const [step, setStep] = useState(null);
  const [prichina, setPrichina] = useState('operator');
  const [saved, setSaved] = useState(null);
  const [manualReceipts, setManualReceipts] = useState(false);

  const isPurchaseTab = tab === 'purchase';

  useEffect(() => {
    api.get('/api/fiscal-readiness')
      .then((r) => setManualReceipts(!!r.manualReceipts))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setPartner(null);
    setPartnerQuery('');
    setItems([]);
    setCart([]);
    setBalance(null);
    setStep(null);
    partnerInput.current?.focus();
  }, [tab]);

  useEffect(() => {
    if (partnerQuery.trim().length < 2) { setPartnerHits([]); return undefined; }
    if (partner && partnerQuery === partner.name) { setPartnerHits([]); return undefined; }
    const t = setTimeout(() => {
      api.get(withQuery('/api/partners', {
        q: partnerQuery, type: isPurchaseTab ? 'supplier' : 'client', limit: 40,
      }))
        .then((r) => setPartnerHits(r.items))
        .catch(() => setPartnerHits([]));
    }, 220);
    return () => clearTimeout(t);
  }, [partnerQuery, partner, isPurchaseTab]);

  function choosePartner(p) {
    setPartner(p);
    setPartnerQuery(p.name);
    setPartnerHits([]);
    setCart([]);
    setBalance(null);
    api.get(`/api/partners/${p.id}`).then(setBalance).catch(() => {});
  }

  function load() {
    if (!partner) { setItems([]); return; }
    setLoading(true);
    setError(null);
    api.get(withQuery(`/api/partners/${partner.id}/purchase-history`, {
      docType: tab, paid: paidFilter, q: productQuery.trim() || undefined,
    }))
      .then((r) => setItems(r.items))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    const t = setTimeout(load, 220);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partner, paidFilter, productQuery, tab]);

  /** Оставащото за връщане, като се извади вече прибавеното в количката. */
  function ostavashto(it) {
    const veche = cart.filter((c) => c.itemId === it.itemId)
      .reduce((s, c) => s + Number(c.returnQty || 0), 0);
    return round2(it.remaining - veche);
  }

  function dobavi(it, kol) {
    const kolichestvo = Math.min(Number(kol) || 0, ostavashto(it));
    if (kolichestvo <= 0) return;
    /*
     * Начинът на плащане за възстановяването взима по подразбиране
     * начина на самата продажба — само при първия ред от ново връщане,
     * за да не пренаписва избор, който операторът вече е направил.
     *
     * Пари назад се връщат само в брой или по банков път. Неплатена
     * разсрочена продажба е трети случай — пари не текат изобщо, връщането
     * урежда дълга („срещу дълга"), а на апарата излиза служебно въведена
     * сума, както в старата програма.
     */
    if (cart.length === 0) setPaymentMethod(podrazbirane(it, isPurchaseTab));
    setStep('doc');
    setCart((cs) => [...cs, {
      key: `${it.itemId}-${Date.now()}`,
      itemId: it.itemId,
      documentId: it.documentId,
      docNumber: it.docNumber,
      unp: it.unp,
      paymentMethod: it.paymentMethod,
      paid: it.paid,
      invoiceNo: it.invoiceNo,
      productId: it.productId,
      batchId: it.batchId,
      warehouseId: it.warehouseId,
      warehouseName: it.warehouseName,
      discountPercent: it.discountPercent,
      description: it.description,
      unit: it.unit,
      vatRate: it.vatRate,
      price: it.price,
      currency: it.currency,
      returnQty: kolichestvo,
    }]);
  }

  const grupi = new Map();
  for (const c of cart) {
    if (!grupi.has(c.documentId)) grupi.set(c.documentId, []);
    grupi.get(c.documentId).push(c);
  }
  const imaFaktura = cart.some((c) => c.invoiceNo);
  const obshtoOsnova = cart.reduce((s, c) => s + round2(Number(c.returnQty) * Number(c.price)), 0);
  const obshtoSDds = cart.reduce((s, c) => s
    + round2(Number(c.returnQty) * Number(c.price) * (1 + Number(c.vatRate) / 100)), 0);

  async function zapishi() {
    setBusy(true);
    setError(null);
    const prichinaText = isPurchaseTab ? null : PRICHINI_STORNO.find(([k]) => k === prichina)[1];
    try {
      let posledenId = null;
      const zapisani = [];
      for (const [documentId, redove] of grupi) {
        const first = redove[0];
        const res = await api.post('/api/documents', {
          docType: isPurchaseTab ? 'purchase_return' : 'sale_return',
          status: 'confirmed',
          // Складът, в който се качва връщаното — този на оригиналната
          // продажба/доставка. Тук се продава само от Варна (виж
          // партидите-памет), затова изборът не се пита на екрана.
          warehouseId: first.warehouseId,
          partnerId: partner.id,
          paymentMethod,
          pricesNet: true,
          issueInvoice: issueInvoice && !!first.invoiceNo,
          invoiceKind: isPurchaseTab ? undefined : 'credit_note',
          note: `Връщане по документ № ${first.docNumber}`
            + (prichinaText ? ` · причина за сторно: ${prichinaText}` : ''),
          items: redove.map((c) => ({
            productId: c.productId,
            batchId: c.batchId,
            returnOfItemId: c.itemId,
            description: c.description,
            unit: c.unit,
            quantity: c.returnQty,
            price: c.price,
            vatRate: c.vatRate,
            discountPercent: c.discountPercent || undefined,
          })),
        });
        posledenId = res.id;
        zapisani.push({
          id: res.id,
          docNumber: res.docNumber,
          origNumber: first.docNumber,
          total: res.total,
          currency: first.currency,
        });
      }
      setDone(`Записано${grupi.size > 1 ? `: ${grupi.size} документа` : ''}.`);
      setCart([]);
      load();
      if (partner) api.get(`/api/partners/${partner.id}`).then(setBalance).catch(() => {});

      /*
       * Преходният период: сторно бонът се бие на ръка на апарата.
       * Прозорецът остава отворен с точната сума, за да не се сбърка, и
       * чак при „Готово" операторът напуска екрана — тогава тръгва и
       * служебно въведената сума, ако връщането е срещу дълга.
       */
      if (!isPurchaseTab && manualReceipts) {
        setSaved({ zapisani, prichina: prichinaText, posledenId });
        setStep('saved');
        return;
      }
      setStep(null);
      if (grupi.size === 1 && posledenId) navigate(`/documents/${posledenId}`);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head"><h1>Връщане на стоки</h1></div>

      <div className="tabs">
        <button className={`tab ${!oldView && !isPurchaseTab ? 'active' : ''}`}
          onClick={() => { setTab('sale'); if (oldView) navigate('/returns'); }}>
          Връщане от клиенти
        </button>
        <button className={`tab ${!oldView && isPurchaseTab ? 'active' : ''}`}
          onClick={() => { setTab('purchase'); if (oldView) navigate('/returns'); }}>
          Връщане към доставчици
        </button>
        <div style={{ flex: 1 }} />
        <button type="button" className={`tab ${oldView === 'sale' ? 'active' : ''}`}
          onClick={() => navigate('/returns?old=sale')}>
          Стари от клиенти
        </button>
        <button type="button" className={`tab ${oldView === 'purchase' ? 'active' : ''}`}
          onClick={() => navigate('/returns?old=purchase')}>
          Стари към доставчици
        </button>
      </div>

      {oldView ? (
        <OldReturns docType={oldView === 'purchase' ? 'purchase_return' : 'sale_return'} />
      ) : (
      <>

      {error && <div className="error">{error}</div>}
      {done && <div className="notice">{done}</div>}

      {partner && balance && (
        <div className="toolbar" style={{ marginBottom: 12 }}>
          {isPurchaseTab ? (
            <span className={`badge ${Number(balance.owed_eur) > 0 ? 'warn' : 'muted'}`}>
              дължим му {money(balance.owed_eur)}
            </span>
          ) : (
            <span className={`badge ${Number(balance.due_eur) > 0 ? 'warn' : 'muted'}`}>
              дължи ни {money(balance.due_eur)}
            </span>
          )}
        </div>
      )}

      <div className="card">
        <div className="card-body">
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label className="field" style={{ position: 'relative', minWidth: 260 }}>
              {isPurchaseTab ? 'Доставчик' : 'Клиент'}
              <input
                ref={partnerInput}
                className="tarsachka"
                type="search"
                autoComplete="off"
                placeholder="търсене по име или ЕИК"
                value={partnerQuery}
                onChange={(e) => { setPartnerQuery(e.target.value); setPartner(null); }}
              />
              {partnerHits.length > 0 && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
                  background: 'var(--surface)', border: '1px solid var(--border)',
                  borderRadius: 6, boxShadow: 'var(--shadow)', overflow: 'hidden',
                }}>
                  {partnerHits.map((p) => (
                    <div key={p.id} style={{ padding: '8px 11px', cursor: 'pointer' }}
                      onMouseDown={(e) => { e.preventDefault(); choosePartner(p); }}>
                      <strong>{p.name}</strong>
                      <span className="muted" style={{ marginLeft: 6 }}>{p.eik ?? ''}</span>
                    </div>
                  ))}
                </div>
              )}
            </label>
            <label className="field" style={{ minWidth: 220 }}>
              Стока
              <input type="search" autoComplete="off" placeholder="име или код" value={productQuery}
                onChange={(e) => setProductQuery(e.target.value)} disabled={!partner} />
            </label>
            <label className="field">
              Платено
              <select value={paidFilter} onChange={(e) => setPaidFilter(e.target.value)}
                disabled={!partner}>
                <option value="all">всички</option>
                <option value="paid">платени</option>
                <option value="unpaid">неплатени</option>
              </select>
            </label>
          </div>
        </div>
      </div>

      {partner && (
        <div className="card">
          <div className="card-head">
            Купено от „{partner.name}"
            {loading && <span className="muted" style={{ marginLeft: 8 }}>зареждане…</span>}
          </div>
          <div className="table-wrap">
            <table className="compact">
              <thead>
                <tr>
                  <th>Стока</th>
                  <th>Партида</th>
                  <th>Документ</th>
                  <th>Дата</th>
                  <th>Плащане</th>
                  <th>Платено</th>
                  <th className="num">Купено</th>
                  <th className="num">Остава</th>
                  <th className="num">Наличност</th>
                  <th className="num">Цена</th>
                  <th style={{ width: 140 }} />
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <ReturnRow key={it.itemId} it={it} ostava={ostavashto(it)} onAdd={dobavi} />
                ))}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={11} className="empty">
                    {isPurchaseTab ? 'Няма доставено с какво да остане за връщане.'
                      : 'Няма продадено с какво да остане за връщане.'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {cart.length > 0 && !step && (
        <div className="notice" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span>Започнато връщане: {cart.length} {cart.length === 1 ? 'ред' : 'реда'} за {money(obshtoSDds)}</span>
          <button className="primary" onClick={() => setStep('doc')}>Отвори връщането</button>
        </div>
      )}

      {step === 'doc' && cart.length > 0 && (
        <div className="modal-backdrop">
        <div className="modal" role="dialog" aria-modal="true" style={{ maxWidth: 820 }}>
          <div className="card-head">
            {isPurchaseTab ? 'Връщане към' : 'Връщане от'} „{partner?.name}"
          </div>
          <div className="table-wrap">
            <table className="compact">
              <thead>
                <tr>
                  <th>Стока</th>
                  <th>От документ</th>
                  <th className="num">Количество</th>
                  <th className="num">Цена без ДДС</th>
                  <th className="num">ДДС</th>
                  <th className="num">Сума</th>
                  <th style={{ width: 40 }} />
                </tr>
              </thead>
              <tbody>
                {cart.map((c) => {
                  const gross = round2(Number(c.returnQty) * Number(c.price));
                  return (
                    <tr key={c.key}>
                      <td>{c.description}</td>
                      <td className="muted">{c.docNumber}</td>
                      <td className="num">{quantity(c.returnQty)} {c.unit}</td>
                      <td className="num">{money(c.price, c.currency)}</td>
                      <td className="num muted">{c.vatRate}%</td>
                      <td className="num">{money(gross, c.currency)}</td>
                      <td>
                        <button className="link"
                          onClick={() => setCart((cs) => cs.filter((x) => x.key !== c.key))}>
                          Премахни
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="card-body">
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <label className="field">
                Начин на плащане (възстановяване)
                <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                  {!isPurchaseTab && cart.some((c) => c.paymentMethod === 'cash_deferred' && !c.paid) && (
                    <option value="cash_deferred">Срещу дълга — пари не се връщат</option>
                  )}
                  {methods.filter((m) => m.code === 'cash' || m.code === 'bank')
                    .map((m) => <option key={m.code} value={m.code}>{m.name}</option>)}
                </select>
              </label>
              {!isPurchaseTab && imaFaktura && (
                <label className="check">
                  <input type="checkbox" checked={issueInvoice}
                    onChange={(e) => setIssueInvoice(e.target.checked)} />
                  издай кредитно известие
                </label>
              )}
              <div className="spacer" />
              <div className="field">
                Данъчна основа
                <div className="field-static">{money(obshtoOsnova)}</div>
              </div>
              <div className="field">
                С ДДС
                <div className="field-static"><strong>{money(obshtoSDds)}</strong></div>
              </div>
            </div>
            {grupi.size > 1 && (
              <p className="hint">
                Редовете идват от {grupi.size} различни документа — ще се запишат като{' '}
                {grupi.size} отделни връщания, всяко към своя оригинал.
              </p>
            )}
          </div>
          <div className="modal-foot">
            <button onClick={() => { setCart([]); setStep(null); }} disabled={busy}>Отказ</button>
            <button onClick={() => setStep(null)} disabled={busy}>Добави още стоки</button>
            {isPurchaseTab ? (
              <button className="primary" disabled={busy} onClick={zapishi}>
                Запиши — връща се {money(obshtoSDds)}
              </button>
            ) : (
              <button className="primary" disabled={busy} onClick={() => setStep('reason')}>
                Сторно бон…
              </button>
            )}
          </div>
        </div>
        </div>
      )}

      {step === 'reason' && (
        <div className="modal-backdrop">
          <div className="modal" role="dialog" aria-modal="true">
            <div className="card-head">Сторно бон — {money(obshtoSDds)}</div>
            <div className="card-body">
              <p className="hint" style={{ marginTop: 0 }}>Причина за сторно бона:</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {PRICHINI_STORNO.map(([k, label]) => (
                  <label key={k} className="check">
                    <input type="radio" name="storno-reason" checked={prichina === k}
                      onChange={() => setPrichina(k)} />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            <div className="modal-foot">
              <button onClick={() => setStep('doc')} disabled={busy}>Назад</button>
              <button className="primary" disabled={busy} onClick={zapishi}>
                {busy ? 'Записване…' : 'OK — запиши'}
              </button>
            </div>
          </div>
        </div>
      )}

      {step === 'saved' && saved && (
        <div className="modal-backdrop">
          <div className="modal" role="dialog" aria-modal="true">
            <div className="card-head">Връщането е записано</div>
            <div className="card-body">
              {saved.zapisani.map((z) => (
                <div key={z.id} style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 18 }}>
                    Изпечатай на апарата <strong>сторно бон за {money(z.total, z.currency)}</strong>
                  </div>
                  <div className="muted">
                    по продажба № {z.origNumber} · причина: {saved.prichina}
                    {' · '}връщане № {z.docNumber}
                  </div>
                </div>
              ))}
              <p className="hint" style={{ marginBottom: 0 }}>
                Номерът, датата и часът на касовия бон се въвеждат на самия апарат.
              </p>
            </div>
            <div className="modal-foot">
              <button className="primary" disabled={busy} onClick={async () => {
                /* Потвърждението, че сторно бонът е изпечатан — едва то
                   пуска служебно въведената сума при връщане срещу дълга. */
                setBusy(true);
                try {
                  for (const z of saved.zapisani) {
                    await api.post(`/api/documents/${z.id}/receipt-printed`);
                  }
                } catch (e) {
                  setError(e.message);
                } finally {
                  setBusy(false);
                }
                const id = saved.zapisani.length === 1 ? saved.posledenId : null;
                setSaved(null);
                setStep(null);
                if (id) navigate(`/documents/${id}`);
              }}>
                Готово — изпечатан е
              </button>
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </>
  );
}

/*
 * Документът за печат/PDF/мейл на връщане (13.10.2026): по фактура —
 * кредитно известие, без фактура — протокол за връщане (receipt).
 */
const formaNaVrashtane = (d) => (d.invoice_no ? 'invoice' : 'receipt');

/**
 * Печат, PDF и мейл на ред от старите връщания (13.10.2026). Мейлът пита
 * преди да тръгне; ако клиентът няма имейл, взема го и го записва в картона.
 */
function DeystviyaVrashtane({ d, onDone }) {
  const [busy, setBusy] = useState(false);
  const form = formaNaVrashtane(d);

  async function mejl() {
    let email = d.partner_email;
    if (!email) {
      email = (window.prompt(`„${d.partner_name}" няма записан имейл. Въведете имейл — ще се запише в картона:`) ?? '').trim();
      if (!email) return;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { onDone('Невалиден имейл адрес', true); return; }
    }
    if (!window.confirm(`Да изпратя документ № ${form === 'invoice' ? d.invoice_no : d.doc_number} до ${email}?`)) return;
    setBusy(true);
    try {
      if (!d.partner_email) await api.patch(`/api/partners/${d.partner_id}`, { email });
      const r = await api.post(`/api/documents/${d.id}/send-mail`, { form });
      onDone(`Изпратено до ${r.to}.`);
    } catch (e) {
      onDone(e.message, true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <span style={{ display: 'inline-flex', gap: 10, whiteSpace: 'nowrap' }}>
      <a href={`/print/document/${d.id}?form=${form}`} target="_blank" rel="noreferrer">Печат</a>
      <a href={`/api/documents/${d.id}/pdf?form=${form}`} download>PDF</a>
      {d.partner_id
        ? <button type="button" className="link" disabled={busy} onClick={mejl}>{busy ? '…' : 'Мейл'}</button>
        : <span className="muted" title="Продажба на каса — няма на кого">Мейл</span>}
    </span>
  );
}

/** Старите връщания — без филтри, само списък и „по-назад". */
function OldReturns({ docType }) {
  const PAGE = 20;
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  // При клиентите — и сторната на цели продажби (13.10.2026).
  const klienti = docType === 'sale_return';

  useEffect(() => { setOffset(0); }, [docType]);

  useEffect(() => {
    setLoading(true);
    api.get(withQuery('/api/documents', {
      type: docType, includeStorno: klienti || undefined, limit: PAGE, offset,
    }))
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [docType, offset]);

  return (
    <>
      {error && <div className="error">{error}</div>}
      {notice && <div className="notice">{notice}</div>}
      <div className="card">
        <div className="table-wrap">
          <table className="compact">
            <thead>
              <tr>
                <th>Дата</th>
                <th>Документ</th>
                {klienti && <th>Вид</th>}
                <th>Контрагент</th>
                <th>Фактура</th>
                <th className="num">Сума</th>
                <th>Състояние</th>
                {klienti && <th />}
              </tr>
            </thead>
            <tbody>
              {data.items.map((d) => (
                <tr key={d.id}>
                  <td className="muted">{date(d.doc_date)}</td>
                  <td><Link to={`/documents/${d.id}`}>{d.doc_number}</Link></td>
                  {klienti && (
                    <td className="muted">{d.doc_type === 'sale' ? 'сторно на продажба' : 'връщане'}</td>
                  )}
                  <td>
                    {d.partner_name
                      ? <Link to={`/documents/${d.id}`}>{d.partner_name}</Link>
                      : <span className="muted">на каса</span>}
                  </td>
                  <td className="muted">{d.invoice_no ?? '—'}</td>
                  <td className="num">{money(d.total, d.currency)}</td>
                  <td><span className={`badge ${STATUS_CLASS[d.status]}`}>{STATUS_LABEL[d.status]}</span></td>
                  {klienti && (
                    <td>
                      <DeystviyaVrashtane d={d} onDone={(msg, isErr) => {
                        if (isErr) { setError(msg); setNotice(null); } else { setNotice(msg); setError(null); }
                      }} />
                    </td>
                  )}
                </tr>
              ))}
              {!loading && data.items.length === 0 && (
                <tr><td colSpan={klienti ? 9 : 6} className="empty">Няма връщания</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <Pager total={data.total} offset={offset} setOffset={setOffset} loading={loading} page={PAGE} />
      </div>
    </>
  );
}

/** Причините за сторно бон — както ги предлага и старата програма. */
const PRICHINI_STORNO = [
  ['operator', 'Операторска грешка'],
  ['return', 'Връщане на стока, рекламация'],
];

/**
 * Начинът на възстановяване по подразбиране според продажбата:
 *   · неплатена разсрочена -> срещу дълга (пари не текат)
 *   · в брой или платена разсрочена -> в брой
 *   · всичко останало -> по банков път
 */
function podrazbirane(it, isPurchase) {
  if (!isPurchase && it.paymentMethod === 'cash_deferred' && !it.paid) return 'cash_deferred';
  if (it.paymentMethod === 'cash' || it.paymentMethod === 'cash_deferred') return 'cash';
  return 'bank';
}

function round2(x) {
  return Math.round(Number(x || 0) * 100) / 100;
}

function ReturnRow({ it, ostava, onAdd }) {
  const [kol, setKol] = useState('');

  useEffect(() => { setKol(ostava > 0 ? String(ostava) : ''); }, [ostava]);

  return (
    <tr className={ostava <= 0 ? 'muted' : undefined}>
      <td>
        <div>{it.description}</div>
        {(it.productCode || it.propertiesText) && (
          <div className="muted" style={{ fontSize: 11 }}>
            {[it.productCode, it.propertiesText].filter(Boolean).join(' · ')}
          </div>
        )}
        {it.groupName && <div className="muted" style={{ fontSize: 11 }}>{it.groupName}</div>}
      </td>
      <td className="muted">{it.batchNo ?? '—'}</td>
      <td className="muted">
        {it.docNumber}
        {it.isTest && <span className="badge" style={{ marginLeft: 6 }}>проба</span>}
      </td>
      <td className="muted">{date(it.docDate)}</td>
      <td className="muted">{PAYMENT_LABEL[it.paymentMethod] ?? it.paymentMethod}</td>
      <td>
        {it.paid
          ? <span className="badge ok">платено</span>
          : <span className="badge warn">неплатено</span>}
      </td>
      <td className="num">{quantity(it.quantity)} {it.unit}</td>
      <td className="num"><strong>{quantity(ostava)}</strong> {it.unit}</td>
      <td className="num muted">
        {it.stockNow == null ? '—' : `${quantity(it.stockNow)} ${it.unit}`}
      </td>
      <td className="num">{money(it.price, it.currency)}</td>
      <td>
        {ostava > 0 && (
          <div style={{ display: 'flex', gap: 6 }}>
            <input className="num qty" style={{ width: 64 }} inputMode="decimal"
              value={kol} onChange={(e) => setKol(e.target.value)} />
            <button type="button" onClick={() => onAdd(it, kol)}>Върни</button>
          </div>
        )}
      </td>
    </tr>
  );
}
